import { useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface GeoCountry {
  id: string;
  iso2: string;
  name: string;
  has_states: boolean;
}

export interface GeoState {
  id: string;
  country_iso2: string;
  name: string;
  code: string | null;
}

export interface GeoCity {
  id: string;
  country_iso2: string;
  state_name: string | null;
  name: string;
}

// Cache keys - moved outside hook to avoid initialization issues
const COUNTRIES_CACHE_KEY = 'geo_countries_cache';
const COUNTRIES_CACHE_TIMESTAMP_KEY = 'geo_countries_cache_timestamp';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

// Helper function to get country name from cache or API (standalone, no hook dependencies)
const getCountryNameFromIso2 = async (countryIso2: string): Promise<string> => {
  try {
    const cachedTimestamp = localStorage.getItem(COUNTRIES_CACHE_TIMESTAMP_KEY);
    const cached = localStorage.getItem(COUNTRIES_CACHE_KEY);
    
    if (cached && cachedTimestamp) {
      const age = Date.now() - parseInt(cachedTimestamp, 10);
      if (age < CACHE_DURATION) {
        const countries = JSON.parse(cached) as GeoCountry[];
        const country = countries.find(c => c.iso2.toUpperCase() === countryIso2.toUpperCase());
        if (country) return country.name;
      }
    }
    
    // If not in cache, try a direct API call for just this country
    const countryApiResponse = await fetch(`https://restcountries.com/v3.1/alpha/${countryIso2}?fields=name`);
    if (countryApiResponse.ok) {
      const countryData = await countryApiResponse.json();
      return countryData.name?.common || countryIso2;
    }
  } catch (err) {
    console.warn("Could not fetch country name, using ISO2:", err);
  }
  return countryIso2;
};

export const useGeoData = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Use refs to avoid dependency issues
  const loadingRef = useRef(false);
  const errorRef = useRef<string | null>(null);

  // Get country flag emoji from ISO2 code
  const getCountryFlag = useCallback((iso2: string): string => {
    const codePoints = iso2
      .toUpperCase()
      .split("")
      .map((char) => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  }, []);

  // Helper function to check if country requires state selection
  const requiresState = useCallback((countryIso2: string): boolean => {
    return ['US', 'CA'].includes(countryIso2.toUpperCase());
  }, []);

  // Get all countries with caching
  const getCountries = useCallback(
    async (search?: string): Promise<GeoCountry[]> => {
      loadingRef.current = true;
      setLoading(true);
      errorRef.current = null;
      setError(null);

      try {
        // Check cache first
        const cachedTimestamp = localStorage.getItem(COUNTRIES_CACHE_TIMESTAMP_KEY);
        const cached = localStorage.getItem(COUNTRIES_CACHE_KEY);
        
        if (cached && cachedTimestamp) {
          const age = Date.now() - parseInt(cachedTimestamp, 10);
          if (age < CACHE_DURATION) {
            console.log("✅ Using cached countries");
            const countries = JSON.parse(cached) as GeoCountry[];
            loadingRef.current = false;
            setLoading(false);
            
            // Apply search filter if provided
            if (search) {
              return countries.filter(c => 
                c.name.toLowerCase().includes(search.toLowerCase()) ||
                c.iso2.toLowerCase().includes(search.toLowerCase())
              );
            }
            return countries;
          }
        }

        console.log("🔄 Fetching countries from database...");
        
        // Query the existing countries table and map to GeoCountry format
        const { data: dbCountries, error: dbError } = await supabase
          .from("countries")
          .select("code, name, flag, is_active")
          .eq("is_active", true)
          .order("name", { ascending: true });

        if (dbError) {
          console.error("❌ Database error:", dbError);
          throw dbError;
        }

        if (dbCountries && dbCountries.length > 0) {
          console.log(`✅ Found ${dbCountries.length} countries in database`);
          
          // Map countries table fields to GeoCountry format
          // Use inline check for has_states instead of requiresState to avoid closure issues
          const mappedCountries: GeoCountry[] = dbCountries.map((c: any) => ({
            id: c.code,
            iso2: c.code,
            name: c.name,
            has_states: ['US', 'CA'].includes(c.code.toUpperCase()),
          }));
          
          console.log(`✅ Mapped ${mappedCountries.length} countries`);
          
          // Cache the results
          localStorage.setItem(COUNTRIES_CACHE_KEY, JSON.stringify(mappedCountries));
          localStorage.setItem(COUNTRIES_CACHE_TIMESTAMP_KEY, Date.now().toString());
          
          loadingRef.current = false;
          setLoading(false);
          
          // Apply search filter if provided
          if (search) {
            return mappedCountries.filter((c: GeoCountry) => 
              c.name.toLowerCase().includes(search.toLowerCase()) ||
              c.iso2.toLowerCase().includes(search.toLowerCase())
            );
          }
          return mappedCountries;
        }

        console.warn("⚠️ No countries found in database");

        // If no data in DB, try direct API call
        console.log("⚠️ No countries in DB, fetching from API...");
        const apiResponse = await fetch('https://restcountries.com/v3.1/all?fields=name,cca2,flags');
        
        if (!apiResponse.ok) {
          throw new Error(`API error: ${apiResponse.statusText}`);
        }

        const apiData = await apiResponse.json();
        const formatted: GeoCountry[] = apiData.map((c: any) => ({
          id: c.cca2,
          iso2: c.cca2,
          name: c.name.common,
          has_states: ['US', 'CA'].includes(c.cca2), // Set based on known countries
        })).sort((a: GeoCountry, b: GeoCountry) => a.name.localeCompare(b.name));

        console.log(`✅ Fetched ${formatted.length} countries from API`);
        
        // Cache the results
        localStorage.setItem(COUNTRIES_CACHE_KEY, JSON.stringify(formatted));
        localStorage.setItem(COUNTRIES_CACHE_TIMESTAMP_KEY, Date.now().toString());

        loadingRef.current = false;
        setLoading(false);
        
        // Apply search filter if provided
        if (search) {
          return formatted.filter(c => 
            c.name.toLowerCase().includes(search.toLowerCase()) ||
            c.iso2.toLowerCase().includes(search.toLowerCase())
          );
        }
        return formatted;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to fetch countries";
        console.error("❌ Error fetching countries:", errorMessage);
        errorRef.current = errorMessage;
        setError(errorMessage);
        loadingRef.current = false;
        setLoading(false);
        throw err;
      }
    },
    [] // No dependencies - completely independent
  );

  // Get states for a country with caching
  const getStates = useCallback(
    async (countryIso2: string, search?: string): Promise<GeoState[]> => {
      loadingRef.current = true;
      setLoading(true);
      errorRef.current = null;
      setError(null);

      try {
        // Check sessionStorage cache first
        const cacheKey = `geo_states_${countryIso2.toUpperCase()}`;
        const cached = sessionStorage.getItem(cacheKey);
        
        if (cached) {
          console.log("✅ Using cached states");
          const states = JSON.parse(cached) as GeoState[];
          loadingRef.current = false;
          setLoading(false);
          
          // Apply search filter if provided
          if (search) {
            return states.filter(s => 
              s.name.toLowerCase().includes(search.toLowerCase()) ||
              (s.code && s.code.toLowerCase().includes(search.toLowerCase()))
            );
          }
          return states;
        }

        console.log(`🔄 Fetching states for ${countryIso2}...`);

        // Try database first
        const { data: dbStates, error: dbError } = await (supabase as any)
          .from("geo_states")
          .select("*")
          .eq("country_iso2", countryIso2.toUpperCase())
          .order("name", { ascending: true });

        if (!dbError && dbStates && dbStates.length > 0) {
          console.log(`✅ Found ${dbStates.length} states in database`);
          
          // Cache the results
          sessionStorage.setItem(cacheKey, JSON.stringify(dbStates));
          loadingRef.current = false;
          setLoading(false);
          
          // Apply search filter if provided
          if (search) {
            return dbStates.filter((s: GeoState) => 
              s.name.toLowerCase().includes(search.toLowerCase()) ||
              (s.code && s.code.toLowerCase().includes(search.toLowerCase()))
            );
          }
          return dbStates;
        }

        // If no data in DB, try direct API call
        console.log("⚠️ No states in DB, fetching from API...");
        
        // Get country name for API - use standalone helper function
        const countryName = await getCountryNameFromIso2(countryIso2);

        const apiResponse = await fetch('https://countriesnow.space/api/v0.1/countries/states', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ country: countryName })
        });

        if (!apiResponse.ok) {
          throw new Error(`API error: ${apiResponse.statusText}`);
        }

        const apiData = await apiResponse.json();
        
        if (apiData.error || !apiData.data || !apiData.data.states) {
          console.log("ℹ️ Country has no states");
          loadingRef.current = false;
          setLoading(false);
          return [];
        }

        const formatted: GeoState[] = apiData.data.states.map((s: any) => ({
          id: `${countryIso2.toUpperCase()}-${s.name}`,
          country_iso2: countryIso2.toUpperCase(),
          name: s.name,
          code: s.state_code || null,
        }));

        console.log(`✅ Fetched ${formatted.length} states from API`);
        
        // Cache the results
        sessionStorage.setItem(cacheKey, JSON.stringify(formatted));
        loadingRef.current = false;
        setLoading(false);
        
        // Apply search filter if provided
        if (search) {
          return formatted.filter(s => 
            s.name.toLowerCase().includes(search.toLowerCase()) ||
            (s.code && s.code.toLowerCase().includes(search.toLowerCase()))
          );
        }
        return formatted;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to fetch states";
        console.error("❌ Error fetching states:", errorMessage);
        errorRef.current = errorMessage;
        setError(errorMessage);
        loadingRef.current = false;
        setLoading(false);
        throw err;
      }
    },
    [] // No dependencies - completely independent
  );

  // Get cities for a country/state with caching and proper API calls
  const getCities = useCallback(
    async (
      countryIso2: string,
      stateName?: string,
      search?: string
    ): Promise<GeoCity[]> => {
      console.log("🏙️ getCities called:", { countryIso2, stateName: stateName || "NONE", search: search || "NONE" });
      loadingRef.current = true;
      setLoading(true);
      errorRef.current = null;
      setError(null);

      // Enforce state requirement for USA & Canada
      if (requiresState(countryIso2) && !stateName) {
        console.error("❌ State required for", countryIso2);
        loadingRef.current = false;
        setLoading(false);
        return [];
      }

      try {
        // Check sessionStorage cache first
        const cacheKey = stateName 
          ? `geo_cities_${countryIso2.toUpperCase()}_${stateName}`
          : `geo_cities_${countryIso2.toUpperCase()}`;
        const cached = sessionStorage.getItem(cacheKey);
        
        if (cached) {
          console.log("✅ Using cached cities");
          const cities = JSON.parse(cached) as GeoCity[];
          loadingRef.current = false;
          setLoading(false);
          
          // Apply search filter if provided
          if (search) {
            return cities.filter(c => 
              c.name.toLowerCase().includes(search.toLowerCase())
            );
          }
          return cities;
        }

        console.log("🔄 Fetching cities from database...");

        // Try database first
        let dbQuery = (supabase as any)
          .from("geo_cities")
          .select("*")
          .eq("country_iso2", countryIso2.toUpperCase())
          .order("name", { ascending: true });

        if (stateName) {
          dbQuery = dbQuery.ilike("state_name", stateName);
        } else {
          dbQuery = dbQuery.is("state_name", null);
        }

        const { data: dbCities, error: dbError } = await dbQuery;

        if (!dbError && dbCities && dbCities.length > 0) {
          console.log(`✅ Found ${dbCities.length} cities in database`);
          
          // Cache the results
          sessionStorage.setItem(cacheKey, JSON.stringify(dbCities));
          loadingRef.current = false;
          setLoading(false);
          
          // Apply search filter if provided
          if (search) {
            return dbCities.filter((c: GeoCity) => 
              c.name.toLowerCase().includes(search.toLowerCase())
            );
          }
          return dbCities;
        }

        // If no data in DB, try direct API call
        console.log("⚠️ No cities in DB, fetching from API...");
        
        // Get country name for API - use standalone helper function
        const countryName = await getCountryNameFromIso2(countryIso2);

        let apiResponse: Response;
        let apiData: any;

        if (stateName) {
          // Fetch cities for state
          console.log(`🔄 Fetching cities for state: ${stateName} in ${countryName}`);
          apiResponse = await fetch('https://countriesnow.space/api/v0.1/countries/state/cities', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              country: countryName, 
              state: stateName 
            })
          });
        } else {
          // Fetch cities for country
          console.log(`🔄 Fetching cities for country: ${countryName}`);
          apiResponse = await fetch('https://countriesnow.space/api/v0.1/countries/cities', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ country: countryName })
          });
        }

        if (!apiResponse.ok) {
          const errorText = await apiResponse.text();
          throw new Error(`API error (${apiResponse.status}): ${errorText}`);
        }

        apiData = await apiResponse.json();
        console.log("📦 API Response:", JSON.stringify(apiData).substring(0, 200));

        // Handle different API response formats
        let citiesArray: string[] = [];
        if (apiData.data) {
          if (Array.isArray(apiData.data)) {
            citiesArray = apiData.data;
          } else if (typeof apiData.data === 'object' && apiData.data.cities) {
            citiesArray = Array.isArray(apiData.data.cities) ? apiData.data.cities : [];
          }
        } else if (apiData.cities && Array.isArray(apiData.cities)) {
          citiesArray = apiData.cities;
        }

        if (citiesArray.length === 0) {
          console.warn(`⚠️ API returned no cities for ${countryName}${stateName ? `, ${stateName}` : ""}`);
          console.warn("📦 Full API response:", apiData);
          loadingRef.current = false;
          setLoading(false);
          return [];
        }

        console.log(`✅ API returned ${citiesArray.length} cities for ${countryName}${stateName ? `, ${stateName}` : ""}`);

        // Format cities
        const formatted: GeoCity[] = citiesArray.map((cityName: string) => ({
          id: `${countryIso2.toUpperCase()}-${stateName || 'none'}-${cityName.toLowerCase().replace(/\s+/g, '-')}`,
          country_iso2: countryIso2.toUpperCase(),
          state_name: stateName || null,
          name: cityName,
        }));

        // Cache the results
        sessionStorage.setItem(cacheKey, JSON.stringify(formatted));
        loadingRef.current = false;
        setLoading(false);
        
        // Apply search filter if provided
        if (search) {
          return formatted.filter(c => 
            c.name.toLowerCase().includes(search.toLowerCase())
          );
        }
        return formatted;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to fetch cities";
        console.error("❌ Error fetching cities:", errorMessage);
        errorRef.current = errorMessage;
        setError(errorMessage);
        loadingRef.current = false;
        setLoading(false);
        throw err;
      }
    },
    [requiresState] // Only requiresState, which is stable
  );

  // Get default country from browser locale
  const getDefaultCountry = useCallback((): string | null => {
    try {
      const locale = navigator.language || (navigator as any).userLanguage;
      const parts = locale.split("-");
      if (parts.length > 1) {
        return parts[1].toUpperCase();
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  return {
    getCountries,
    getStates,
    getCities,
    getCountryFlag,
    getDefaultCountry,
    requiresState,
    loading,
    error,
  };
};
